/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, March 28th 2022, 2:21:40 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { isEmpty } from "lodash";
import {
  useAPIFetchItem,
  useAPIMutation,
  useAPIFetchList,
  UseFetchListArgs,
  APISERVICEPATHS,
  useAPIRequest,
  useAPIForm,
  UseAPIFormSpec,
} from "lib/api";
import {
  FormField,
  FormFieldValidationSchema,
  ChoiceOptionGroup,
  ChoiceOption,
  FieldValidator,
  FieldValidatorValidationSchema,
  FIELD_TYPES_API_RESPONSE,
  FIELD_TYPES,
} from "lib/schemas";
import { PrimaryKeyValue, APISearchParameters } from "lib/types";
import { useDeleteOSItem } from "version-control/services";
import { useFormBuilderStore } from "form-builder/stores";

const fieldsAPIServicePath = APISERVICEPATHS.formFields;

const fieldReorderAPIServicePath = APISERVICEPATHS.formFieldsReorder;

const useFormFieldAPIForm = (
  formFieldFetchId: PrimaryKeyValue<FormField>,
  formField: FormField,
  updateFieldDataFromAPI: (object: FormField) => void
) =>
  useAPIForm({
    apiFormDefaultValues: formField,
    apiFormDataValidationSchema: FormFieldValidationSchema,
    apiInitialFetchId: formFieldFetchId,
    apiServicePath: fieldsAPIServicePath,
    setAPIFormResponse: updateFieldDataFromAPI,
    primaryKey: "id",
    formResetDependencyArray: [formField.order, formField.slug],
    fieldsToResetOnFormReset: ["order", "slug"],
  });

export const useFormFieldsAPI = () => ({
  useFetch: (filters?: APISearchParameters<FormField>) =>
    useAPIFetchItem<FormField>({
      apiServicePath: fieldsAPIServicePath,
      apiQueryParams: { ...filters },
      makeRequestOnInitialMount: false,
    }),
  useDelete: () => useDeleteOSItem(fieldsAPIServicePath),
  useReorderField: () => useAPIMutation<FormField>({ apiServicePath: fieldReorderAPIServicePath }),
  useFormFieldAPIForm,
});

// Field Choice Options API

const fieldChoiceOptionGroupAPIServicePath = APISERVICEPATHS.fieldChoiceOptionGroup;

export const useAPIListChoiceOptionGroups = () =>
  useAPIFetchList<ChoiceOptionGroup>({
    apiServicePath: fieldChoiceOptionGroupAPIServicePath,
    makeRequestOnInitialMount: false,
  });

export const useAPIFetchChoiceOptionGroup = () =>
  useAPIFetchItem<ChoiceOptionGroup>({
    apiServicePath: fieldChoiceOptionGroupAPIServicePath,
    makeRequestOnInitialMount: false,
  });

export const useAPIMutateChoiceOptionGroup = () =>
  useAPIMutation<ChoiceOptionGroup>({
    apiServicePath: fieldChoiceOptionGroupAPIServicePath,
  });

const fieldChoiceOptionAPIServicePath = APISERVICEPATHS.fieldChoiceOption;

export const useAPIListChoiceOptions = (
  args: Omit<UseFetchListArgs<ChoiceOption>, "apiServicePath" | "makeRequestOnInitialMount">
) =>
  useAPIFetchList<ChoiceOption>({
    apiServicePath: fieldChoiceOptionAPIServicePath,
    makeRequestOnInitialMount: false,
    ...args,
  });

export const useAPIMutateChoiceOption = () =>
  useAPIMutation<ChoiceOption>({
    apiServicePath: fieldChoiceOptionAPIServicePath,
  });

export const useAPIDeleteChoiceOption = () => useDeleteOSItem(fieldChoiceOptionAPIServicePath);

const fieldValidatorsAPIServicePath = APISERVICEPATHS.fieldValidators;

interface UseFieldValidatorFormSpec {
  fieldValidatorId: FieldValidator["id"];
  fieldValidatorDefault: FieldValidator;
  updateFieldValidatorFromAPICallback: UseAPIFormSpec<FieldValidator>["setAPIFormResponse"];
}

export const useFieldValidatorsAPI = () => ({
  useList: () =>
    useAPIRequest<FieldValidator[]>({
      apiServicePath: fieldValidatorsAPIServicePath,
      makeRequestOnInitialMount: false,
    }),
  useFieldValidatorAPIForm: (args: UseFieldValidatorFormSpec) => {
    return useAPIForm({
      apiServicePath: fieldValidatorsAPIServicePath,
      apiFormDataValidationSchema: FieldValidatorValidationSchema,
      apiFormDefaultValues: args?.fieldValidatorDefault,
      apiInitialFetchId: args?.fieldValidatorId,
      primaryKey: "id",
      setAPIFormResponse: args?.updateFieldValidatorFromAPICallback,
    });
  },
  useDelete: () => useDeleteOSItem(fieldValidatorsAPIServicePath),
});

const useFetchAllFieldTypes = () => {
  const { setFieldTypes, fieldTypes } = useFormBuilderStore();
  const { makeRequest, makeRequestCleanUp, ...otherProperties } = useAPIRequest<FIELD_TYPES_API_RESPONSE>({
    apiServicePath: APISERVICEPATHS.fieldTypes,
    makeRequestOnInitialMount: false,
  });

  const fetchTypes = React.useCallback(
    (setValidAPIResponse: (customFieldTypes: FIELD_TYPES_API_RESPONSE) => void) => {
      makeRequest({ setValidAPIResponse });
    },
    [makeRequest]
  );

  const doFieldTypesFetch = !isEmpty(fieldTypes);

  // We only need to set the customFieldTypes in the store and set them once.
  React.useEffect(() => {
    if (doFieldTypesFetch) return;

    fetchTypes(setFieldTypes);
    return makeRequestCleanUp;
  }, [fetchTypes, makeRequestCleanUp, setFieldTypes, doFieldTypesFetch]);

  return { ...otherProperties, fieldTypes };
};

const useFetchCustomFieldTypes = () => {
  const { fieldTypes, ...otherProperties } = useFetchAllFieldTypes();
  const customFieldTypes = Object.keys(fieldTypes).reduce((prev, curr: any) => {
    if (!Object.values(FIELD_TYPES).includes(curr)) {
      return { ...prev, [curr]: fieldTypes[curr] };
    }
    return {};
  }, {});

  return { ...otherProperties, customFieldTypes };
};

export const useFieldTypesAPI = () => ({
  useFetchAllFieldTypes,
  useFetchCustomFieldTypes,
});
